import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from '../../translations';
import LanguageToggle from './LanguageToggle';

// Legal Modal Component
const LegalModal = ({ isOpen, onClose, title, content }) => {
  if (!isOpen) return null;
  
  return (
    <div 
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="glass-card w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-4 border-b border-blue-400/30">
          <h3 className="text-xl font-bold text-white">{title}</h3>
          <button 
            onClick={onClose}
            className="text-blue-300 hover:text-white text-2xl leading-none"
            data-testid="close-legal-modal"
          >
            ×
          </button>
        </div>
        <div className="p-6 overflow-y-auto flex-1">
          <div className="text-blue-100 text-sm leading-relaxed whitespace-pre-line">
            {content}
          </div>
        </div>
        <div className="p-4 border-t border-blue-400/30">
          <button 
            onClick={onClose}
            className="btn-primary w-full"
            data-testid="accept-legal-modal"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default function Register() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Personal Information
    email: '',
    password: '',
    full_name: '',
    phone: '',
    role: 'client',
    nationality: '',
    address: '',
    postal_code: '',
    city: '',
    // Step 2: Physical Information
    birth_date: '',
    gender: '',
    height: '',
    weight: '',
    // Emergency Contact (Recommended for safety)
    emergency_contact_name: '',
    emergency_contact_relationship: '',
    emergency_contact_phone: '',
    // Step 3: Training Information
    injury_history_note: '',
    referral_source: '',
    training_objectives: '',
    // Step 4: Preferences
    payment_preference: '',
    schedule_preferences: {
      time_slots: [],
      days: []
    },
    // ERP Phase 4: Booking Mode & Fee
    booking_mode: '',  // 'monthly_fixed' or 'booking_flex'
    monthly_fee: '',   // Only for monthly_fixed
    // Legal compliance
    terms_accepted: false,
    privacy_policy_accepted: false
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const { register } = useAuth();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const navigate = useNavigate();
  
  // Legal texts (exact content from requirements)
  const TERMS_CONTENT = `Declaro que los datos proporcionados son veraces y que he informado al entrenador de cualquier lesión, patología o condición médica relevante para la práctica de actividad física.

Soy consciente de que la práctica de ejercicio físico implica un riesgo inherente de lesión y acepto participar bajo mi propia responsabilidad, eximiendo a Betera Training Center y a su personal de cualquier responsabilidad derivada de un uso indebido de las instalaciones o del incumplimiento de las indicaciones profesionales recibidas.

Me comprometo a respetar las normas del centro y a seguir las indicaciones del entrenador durante las sesiones de entrenamiento.`;

  const PRIVACY_CONTENT = `De conformidad con el Reglamento (UE) 2016/679 (RGPD), autorizo a Betera Training Center al tratamiento de mis datos personales con la finalidad de gestionar mi relación como cliente, la planificación de entrenamientos, el seguimiento deportivo y las comunicaciones relacionadas con el servicio.

Los datos no serán cedidos a terceros salvo obligación legal y se conservarán mientras se mantenga la relación contractual.

He sido informado de que puedo ejercer mis derechos de acceso, rectificación, supresión, limitación y oposición mediante solicitud al responsable del tratamiento.`;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (category, value) => {
    const current = formData.schedule_preferences[category] || [];
    const updated = current.includes(value)
      ? current.filter(item => item !== value)
      : [...current, value];
    
    setFormData({
      ...formData,
      schedule_preferences: {
        ...formData.schedule_preferences,
        [category]: updated
      }
    });
  };

  const nextStep = () => {
    setError('');
    setStep(step + 1);
  };

  const prevStep = () => {
    setError('');
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate for clients
    if (formData.role === 'client') {
      // ERP Phase 4: Validate booking_mode is selected
      if (!formData.booking_mode) {
        setError(language === 'es' 
          ? 'Por favor, selecciona cómo prefieres entrenar (Mensual o Por sesiones).'
          : 'Please select how you prefer to train (Monthly or Per session).');
        return;
      }
      
      // Validate legal acceptance
      if (!formData.terms_accepted) {
        setError(language === 'es' 
          ? 'Debes aceptar los términos y condiciones para continuar.'
          : 'You must accept the terms and conditions to continue.');
        return;
      }
      if (!formData.privacy_policy_accepted) {
        setError(language === 'es' 
          ? 'Debes aceptar la política de privacidad para continuar.'
          : 'You must accept the privacy policy to continue.');
        return;
      }
    }
    
    setLoading(true);
    setError('');

    // Build submission data with proper types
    // Convert empty strings to null for optional fields to avoid Pydantic validation errors
    const cleanedFormData = Object.fromEntries(
      Object.entries(formData).map(([key, value]) => {
        // Keep non-string values as-is (booleans, objects, etc.)
        if (typeof value !== 'string') return [key, value];
        // Convert empty strings to null
        if (value === '') return [key, null];
        return [key, value];
      })
    );
    
    const submissionData = {
      ...cleanedFormData,
      // ERP Phase 4: Ensure booking_mode and monthly_fee are properly set
      booking_mode: formData.booking_mode || null,
      monthly_fee: formData.monthly_fee ? parseFloat(formData.monthly_fee) : null,
      // Convert numeric fields properly
      height: formData.height ? parseFloat(formData.height) : null,
      weight: formData.weight ? parseFloat(formData.weight) : null,
      // Set default client_status for new clients
      client_status: formData.role === 'client' ? 'onboarding' : null,
      // Legal timestamps and version
      terms_accepted_at: formData.terms_accepted ? new Date().toISOString() : null,
      privacy_policy_accepted_at: formData.privacy_policy_accepted ? new Date().toISOString() : null,
      legal_version: (formData.terms_accepted || formData.privacy_policy_accepted) ? 'v1.0' : null
    };

    const result = await register(submissionData);
    if (result.success) {
      navigate('/dashboard');
    } else {
      // Handle error - make sure it's a string
      const errorMessage = typeof result.error === 'string' 
        ? result.error 
        : result.error?.detail || result.error?.message || 'Registration failed. Please try again.';
      setError(errorMessage);
    }
    setLoading(false);
  };

  const renderStepIndicator = () => (
    <div className="flex justify-center mb-8">
      {[1, 2, 3, 4].map((s) => (
        <div key={s} className="flex items-center">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            step >= s ? 'bg-blue-500 text-white' : 'bg-gray-600 text-gray-400'
          }`}>
            {s}
          </div>
          {s < 4 && <div className={`w-16 h-1 ${step > s ? 'bg-blue-500' : 'bg-gray-600'}`} />}
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 flex items-center justify-center p-4">
      <div className="glass-card w-full max-w-3xl p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-end mb-4">
          <LanguageToggle />
        </div>
        
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_workout-central-9/artifacts/1boxowhf_logo%20btc.PNG" 
              alt="Bétera Training Center" 
              className="h-32 w-32 object-contain"
            />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">{t('join_betera')}</h1>
          <p className="text-blue-200">{t('start_transformation')}</p>
        </div>

        {renderStepIndicator()}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Step 1: Personal Information */}
          {step === 1 && (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-white mb-4">{t('registration_step_1')}</h2>
              <p className="text-blue-300 text-sm mb-4">{t('only_email_password_required')}</p>
              
              <div>
                <label className="block text-blue-200 text-sm font-medium mb-2">{t('full_name')} *</label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  className="input-field"
                  required
                />
              </div>

              <div>
                <label className="block text-blue-200 text-sm font-medium mb-2">{t('email')} *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="input-field"
                  required
                />
              </div>

              <div>
                <label className="block text-blue-200 text-sm font-medium mb-2">{t('password')} *</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="input-field"
                  required
                  minLength="6"
                />
              </div>

              <div>
                <label className="block text-blue-200 text-sm font-medium mb-2">{t('phone')} ({t('optional')})</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-blue-200 text-sm font-medium mb-2">{t('nationality')} ({t('optional')})</label>
                <select
                  name="nationality"
                  value={formData.nationality}
                  onChange={handleChange}
                  className="input-field"
                >
                  <option value="">{t('select')}</option>
                  <option value="España">España</option>
                  <option value="Argentina">Argentina</option>
                  <option value="Colombia">Colombia</option>
                  <option value="México">México</option>
                  <option value="Chile">Chile</option>
                  <option value="Perú">Perú</option>
                  <option value="Venezuela">Venezuela</option>
                  <option value="Ecuador">Ecuador</option>
                  <option value="Uruguay">Uruguay</option>
                  <option value="Bolivia">Bolivia</option>
                  <option value="Paraguay">Paraguay</option>
                  <option value="Costa Rica">Costa Rica</option>
                  <option value="Cuba">Cuba</option>
                  <option value="República Dominicana">República Dominicana</option>
                  <option value="Guatemala">Guatemala</option>
                  <option value="Honduras">Honduras</option>
                  <option value="El Salvador">El Salvador</option>
                  <option value="Nicaragua">Nicaragua</option>
                  <option value="Panamá">Panamá</option>
                  <option value="Puerto Rico">Puerto Rico</option>
                  <option value="Francia">Francia</option>
                  <option value="Italia">Italia</option>
                  <option value="Alemania">Alemania</option>
                  <option value="Portugal">Portugal</option>
                  <option value="Reino Unido">Reino Unido</option>
                  <option value="Estados Unidos">Estados Unidos</option>
                  <option value="Canadá">Canadá</option>
                  <option value="Brasil">Brasil</option>
                  <option value="Otro">Otro</option>
                </select>
              </div>

              <div>
                <label className="block text-blue-200 text-sm font-medium mb-2">{t('address')}</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="input-field"
                  placeholder={language === 'es' ? 'Calle, número...' : 'Street, number...'}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-blue-200 text-sm font-medium mb-2">{language === 'es' ? 'Código Postal' : 'Postal Code'}</label>
                  <input
                    type="text"
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleChange}
                    className="input-field"
                    placeholder={language === 'es' ? 'Ej: 46117' : 'e.g., 46117'}
                  />
                </div>

                <div>
                  <label className="block text-blue-200 text-sm font-medium mb-2">{language === 'es' ? 'Población' : 'City'}</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="input-field"
                    placeholder={language === 'es' ? 'Ej: Bétera' : 'e.g., Valencia'}
                  />
                </div>
              </div>

              {/* Role is always CLIENT for public sign-ups. Staff accounts are
                  created by administrators — never selectable here. The backend
                  also force-sets role=client (SEC-001), so this is defence in depth. */}
            </div>
          )}

          {/* Step 2: Physical Information */}
          {step === 2 && (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-white mb-4">{t('registration_step_2')}</h2>
              
              <div>
                <label className="block text-blue-200 text-sm font-medium mb-2">{t('birth_date')}</label>
                <input
                  type="date"
                  name="birth_date"
                  value={formData.birth_date}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-blue-200 text-sm font-medium mb-2">{t('gender')}</label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  className="input-field"
                >
                  <option value="">{t('select')}</option>
                  <option value="male">{t('male')}</option>
                  <option value="female">{t('female')}</option>
                  <option value="prefer_not_to_say">{t('prefer_not_to_say')}</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-blue-200 text-sm font-medium mb-2">{t('height')} (cm)</label>
                  <input
                    type="number"
                    name="height"
                    value={formData.height}
                    onChange={handleChange}
                    className="input-field"
                    step="0.1"
                  />
                </div>

                <div>
                  <label className="block text-blue-200 text-sm font-medium mb-2">{t('weight')}</label>
                  <input
                    type="number"
                    name="weight"
                    value={formData.weight}
                    onChange={handleChange}
                    className="input-field"
                    step="0.1"
                  />
                </div>
              </div>
              
              {/* Emergency Contact Section - Recommended for Safety */}
              <div className="border-t border-blue-400/30 pt-4 mt-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-xl">🚨</span>
                  <h3 className="text-lg font-semibold text-white">{language === 'es' ? 'Contacto de Emergencia' : 'Emergency Contact'}</h3>
                  <span className="text-blue-300 text-xs bg-blue-500/20 px-2 py-1 rounded">
                    {language === 'es' ? 'Recomendado' : 'Recommended'}
                  </span>
                </div>
                <p className="text-blue-300 text-sm mb-4">
                  {language === 'es' 
                    ? 'Por tu seguridad, te recomendamos indicar un contacto de emergencia.'
                    : 'For your safety, we recommend providing an emergency contact.'}
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-blue-200 text-sm font-medium mb-2">
                      {language === 'es' ? 'Nombre del contacto' : 'Contact Name'}
                    </label>
                    <input
                      type="text"
                      name="emergency_contact_name"
                      value={formData.emergency_contact_name}
                      onChange={handleChange}
                      className="input-field"
                      placeholder={language === 'es' ? 'Ej: María García' : 'e.g., John Smith'}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-blue-200 text-sm font-medium mb-2">
                      {language === 'es' ? 'Relación' : 'Relationship'}
                    </label>
                    <select
                      name="emergency_contact_relationship"
                      value={formData.emergency_contact_relationship}
                      onChange={handleChange}
                      className="input-field"
                    >
                      <option value="">{t('select')}</option>
                      <option value="spouse">{language === 'es' ? 'Cónyuge / Pareja' : 'Spouse / Partner'}</option>
                      <option value="parent">{language === 'es' ? 'Padre / Madre' : 'Parent'}</option>
                      <option value="sibling">{language === 'es' ? 'Hermano/a' : 'Sibling'}</option>
                      <option value="child">{language === 'es' ? 'Hijo/a' : 'Child'}</option>
                      <option value="friend">{language === 'es' ? 'Amigo/a' : 'Friend'}</option>
                      <option value="other">{language === 'es' ? 'Otro' : 'Other'}</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-blue-200 text-sm font-medium mb-2">
                      {language === 'es' ? 'Teléfono' : 'Phone'}
                    </label>
                    <input
                      type="tel"
                      name="emergency_contact_phone"
                      value={formData.emergency_contact_phone}
                      onChange={handleChange}
                      className="input-field"
                      placeholder={language === 'es' ? 'Ej: +34 612 345 678' : 'e.g., +1 555-123-4567'}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Training Information */}
          {step === 3 && (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-white mb-4">{t('registration_step_3')}</h2>
              
              <div>
                <label className="block text-blue-200 text-sm font-medium mb-2">{t('injury_history')}</label>
                <textarea
                  name="injury_history_note"
                  value={formData.injury_history_note}
                  onChange={handleChange}
                  className="input-field"
                  rows="3"
                  placeholder={t('injury_history')}
                />
              </div>

              <div>
                <label className="block text-blue-200 text-sm font-medium mb-2">{t('referral_source')}</label>
                <select
                  name="referral_source"
                  value={formData.referral_source}
                  onChange={handleChange}
                  className="input-field"
                >
                  <option value="">{t('select')}</option>
                  <option value="social_media">{t('social_media')}</option>
                  <option value="recommendation">{t('recommendation')}</option>
                  <option value="web_search">{t('web_search')}</option>
                  <option value="other">{t('other')}</option>
                </select>
              </div>

              <div>
                <label className="block text-blue-200 text-sm font-medium mb-2">{t('training_objectives')}</label>
                <textarea
                  name="training_objectives"
                  value={formData.training_objectives}
                  onChange={handleChange}
                  className="input-field"
                  rows="4"
                  placeholder={t('training_objectives')}
                />
              </div>
            </div>
          )}

          {/* Step 4: Preferences & Legal */}
          {step === 4 && (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-white mb-4">{t('registration_step_4')}</h2>
              
              {/* ERP Phase 4: Booking Mode Selection (REQUIRED) */}
              <div className="bg-blue-900/30 p-4 rounded-lg border border-blue-400/30">
                <label className="block text-blue-200 text-sm font-medium mb-3">
                  {language === 'es' ? '¿Cómo prefieres entrenar?' : 'How do you prefer to train?'} *
                </label>
                <div className="space-y-3">
                  <label className="flex items-start space-x-3 cursor-pointer p-3 rounded-lg hover:bg-blue-800/30 transition-colors">
                    <input
                      type="radio"
                      name="booking_mode"
                      value="monthly_fixed"
                      checked={formData.booking_mode === 'monthly_fixed'}
                      onChange={handleChange}
                      className="form-radio text-blue-500 mt-1"
                    />
                    <div>
                      <span className="text-white font-medium block">
                        {language === 'es' ? '📅 Mensual (Cuota fija)' : '📅 Monthly (Fixed fee)'}
                      </span>
                      <span className="text-blue-300 text-xs">
                        {language === 'es' 
                          ? 'Pago mensual fijo. El entrenador asigna tus sesiones semanales.'
                          : 'Fixed monthly payment. Trainer assigns your weekly sessions.'}
                      </span>
                    </div>
                  </label>
                  <label className="flex items-start space-x-3 cursor-pointer p-3 rounded-lg hover:bg-blue-800/30 transition-colors">
                    <input
                      type="radio"
                      name="booking_mode"
                      value="booking_flex"
                      checked={formData.booking_mode === 'booking_flex'}
                      onChange={handleChange}
                      className="form-radio text-blue-500 mt-1"
                    />
                    <div>
                      <span className="text-white font-medium block">
                        {language === 'es' ? '🎫 Por sesiones (Bono)' : '🎫 Per session (Package)'}
                      </span>
                      <span className="text-blue-300 text-xs">
                        {language === 'es' 
                          ? 'Compra un bono de sesiones y reserva cuando quieras.'
                          : 'Buy a session package and book whenever you want.'}
                      </span>
                    </div>
                  </label>
                </div>
                
                {/* Monthly Fee / Package Price Field (shown for BOTH modes) */}
                {formData.booking_mode && (
                  <div className="mt-4 pt-4 border-t border-blue-400/20">
                    <label className="block text-blue-200 text-sm font-medium mb-2">
                      {formData.booking_mode === 'monthly_fixed'
                        ? (language === 'es' ? 'Cuota mensual acordada (€)' : 'Agreed monthly fee (€)')
                        : (language === 'es' ? 'Precio del bono / sesión (€)' : 'Package / session price (€)')
                      }
                    </label>
                    <input
                      type="number"
                      name="monthly_fee"
                      value={formData.monthly_fee}
                      onChange={handleChange}
                      className="input-field w-full md:w-1/2"
                      placeholder={formData.booking_mode === 'monthly_fixed'
                        ? (language === 'es' ? 'Ej: 150' : 'e.g., 150')
                        : (language === 'es' ? 'Ej: 120 (bono 8 sesiones)' : 'e.g., 120 (8 session package)')
                      }
                      min="0"
                      step="0.01"
                    />
                    <p className="text-blue-400 text-xs mt-1">
                      {formData.booking_mode === 'monthly_fixed'
                        ? (language === 'es' 
                            ? 'Puedes dejarlo vacío si aún no lo has acordado con el entrenador.'
                            : 'You can leave this empty if not yet agreed with your trainer.')
                        : (language === 'es'
                            ? 'Indica el precio del bono o por sesión acordado.'
                            : 'Enter the agreed package price or per-session rate.')
                      }
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-blue-200 text-sm font-medium mb-3">{t('time_slots')}</label>
                <div className="grid grid-cols-2 gap-2">
                  {['morning', 'midday', 'afternoon', 'evening'].map((slot) => (
                    <label key={slot} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.schedule_preferences.time_slots.includes(slot)}
                        onChange={() => handleCheckboxChange('time_slots', slot)}
                        className="form-checkbox text-blue-500"
                      />
                      <span className="text-white">{t(slot)}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-blue-200 text-sm font-medium mb-3">{t('preferred_days')}</label>
                <div className="grid grid-cols-2 gap-2">
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].map((day) => (
                    <label key={day} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.schedule_preferences.days.includes(day)}
                        onChange={() => handleCheckboxChange('days', day)}
                        className="form-checkbox text-blue-500"
                      />
                      <span className="text-white">{t(day)}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Legal Compliance Section */}
              <div className="border-t border-blue-400/30 pt-6 mt-6">
                <h3 className="text-lg font-semibold text-white mb-4">Condiciones legales *</h3>
                
                {/* Terms & Conditions Checkbox */}
                <div className="mb-4">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.terms_accepted}
                      onChange={(e) => setFormData({...formData, terms_accepted: e.target.checked})}
                      className="form-checkbox text-blue-500 mt-1"
                      data-testid="terms-checkbox"
                    />
                    <span className="text-white text-sm">
                      Acepto los términos y condiciones del servicio{' '}
                      <button
                        type="button"
                        onClick={() => setShowTermsModal(true)}
                        className="text-blue-400 hover:text-blue-300 underline"
                        data-testid="view-terms-link"
                      >
                        (Ver detalles)
                      </button>
                    </span>
                  </label>
                </div>
                
                {/* Privacy Policy Checkbox */}
                <div className="mb-4">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.privacy_policy_accepted}
                      onChange={(e) => setFormData({...formData, privacy_policy_accepted: e.target.checked})}
                      className="form-checkbox text-blue-500 mt-1"
                      data-testid="privacy-checkbox"
                    />
                    <span className="text-white text-sm">
                      Acepto la política de privacidad{' '}
                      <button
                        type="button"
                        onClick={() => setShowPrivacyModal(true)}
                        className="text-blue-400 hover:text-blue-300 underline"
                        data-testid="view-privacy-link"
                      >
                        (Ver detalles)
                      </button>
                    </span>
                  </label>
                </div>
                
                {/* Health Notice (informational only, no checkbox) */}
                <div className="bg-yellow-500/10 border border-yellow-400/30 rounded-lg p-4 mt-4">
                  <p className="text-yellow-200 text-sm">
                    <strong>Aviso importante:</strong><br />
                    Si padeces alguna lesión, enfermedad crónica o condición médica que pueda verse afectada por la práctica de ejercicio físico, informa previamente al entrenador antes de iniciar cualquier actividad.
                  </p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-500/20 border border-red-400 text-red-200 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center pt-4">
            {step > 1 && (
              <button
                type="button"
                onClick={prevStep}
                className="btn-secondary"
              >
                {language === 'es' ? 'Atrás' : 'Back'}
              </button>
            )}
            
            <div className="flex-1"></div>

            {step < 4 ? (
              <div className="flex space-x-2">
                {step > 1 && (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="btn-secondary"
                  >
                    {t('skip')} →
                  </button>
                )}
                <button
                  type="button"
                  onClick={nextStep}
                  className="btn-primary"
                >
                  {t('continue')}
                </button>
              </div>
            ) : (
              <button
                type="submit"
                disabled={loading}
                className="btn-primary"
              >
                {loading ? '...' : t('complete_registration')}
              </button>
            )}
          </div>
        </form>

        <div className="mt-6 text-center">
          <p className="text-blue-200">
            {t('already_have_account')}{' '}
            <Link to="/login" className="text-blue-400 hover:text-blue-300 font-medium">
              {t('sign_in')}
            </Link>
          </p>
        </div>
      </div>
      
      {/* Legal Modals */}
      <LegalModal 
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        title="Términos y condiciones del servicio"
        content={TERMS_CONTENT}
      />
      <LegalModal 
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        title="Política de privacidad"
        content={PRIVACY_CONTENT}
      />
    </div>
  );
};
